import { ValidationRule, ValidationError, FieldType } from '../schema/types';

export class FormValidator {
  /**
   * Validates a field value against its validation rules
   */
  static validateField(
    fieldId: string,
    conceptId: string,
    columnId: string,
    value: any,
    fieldType: FieldType,
    rules: ValidationRule[] = []
  ): ValidationError[] {
    const errors: ValidationError[] = [];

    for (const rule of rules) {
      const error = this.validateRule(fieldId, conceptId, columnId, value, fieldType, rule);
      if (error) {
        errors.push(error);
      }
    }

    return errors;
  }

  /**
   * Validates a single rule against a value
   */
  private static validateRule(
    fieldId: string,
    conceptId: string,
    columnId: string,
    value: any,
    _fieldType: FieldType,
    rule: ValidationRule
  ): ValidationError | null {
    const isEmpty = value === null || value === undefined || value === '';

    switch (rule.type) {
      case 'required':
        if (isEmpty) {
          return this.createError(fieldId, conceptId, columnId, rule);
        }
        break;

      case 'min':
        if (!isEmpty && typeof value === 'number' && value < rule.value) {
          return this.createError(fieldId, conceptId, columnId, rule);
        }
        break;

      case 'max':
        if (!isEmpty && typeof value === 'number' && value > rule.value) {
          return this.createError(fieldId, conceptId, columnId, rule);
        }
        break;

      case 'minLength':
        if (!isEmpty && typeof value === 'string' && value.length < rule.value) {
          return this.createError(fieldId, conceptId, columnId, rule);
        }
        break;

      case 'maxLength':
        if (!isEmpty && typeof value === 'string' && value.length > rule.value) {
          return this.createError(fieldId, conceptId, columnId, rule);
        }
        break;

      case 'pattern':
        if (!isEmpty && typeof value === 'string') {
          const regex = new RegExp(rule.value);
          if (!regex.test(value)) {
            return this.createError(fieldId, conceptId, columnId, rule);
          }
        }
        break;

      case 'email':
        if (!isEmpty && typeof value === 'string') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            return this.createError(fieldId, conceptId, columnId, rule);
          }
        }
        break;

      case 'url':
        if (!isEmpty && typeof value === 'string') {
          try {
            new URL(value);
          } catch {
            return this.createError(fieldId, conceptId, columnId, rule);
          }
        }
        break;

      case 'custom':
        // Custom validation would be handled by consuming application
        break;

      default:
        console.warn(`Unknown validation rule type: ${rule.type}`);
    }

    return null;
  }

  /**
   * Creates a validation error object
   */
  private static createError(
    fieldId: string,
    conceptId: string,
    columnId: string,
    rule: ValidationRule
  ): ValidationError {
    return {
      fieldId,
      conceptId,
      columnId,
      message: rule.message,
      severity: rule.severity || 'error',
      rule
    };
  }

  /**
   * Validates data type conversion
   */
  static validateDataType(value: any, fieldType: FieldType): { valid: boolean; convertedValue?: any; error?: string } {
    if (value === null || value === undefined || value === '') {
      return { valid: true, convertedValue: value };
    }

    switch (fieldType) {
      case 'number':
      case 'decimal':
      case 'currency':
      case 'percentage': {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return { valid: false, error: 'Invalid number format' };
        }
        return { valid: true, convertedValue: numValue };
      }

      case 'date': {
        const dateValue = new Date(value);
        if (isNaN(dateValue.getTime())) {
          return { valid: false, error: 'Invalid date format' };
        }
        return { valid: true, convertedValue: dateValue.toISOString().split('T')[0] };
      }

      case 'datetime': {
        const datetimeValue = new Date(value);
        if (isNaN(datetimeValue.getTime())) {
          return { valid: false, error: 'Invalid datetime format' };
        }
        return { valid: true, convertedValue: datetimeValue.toISOString() };
      }

      case 'boolean':
        if (typeof value === 'boolean') {
          return { valid: true, convertedValue: value };
        }
        if (typeof value === 'string') {
          const boolValue = value.toLowerCase();
          if (boolValue === 'true' || boolValue === '1') {
            return { valid: true, convertedValue: true };
          }
          if (boolValue === 'false' || boolValue === '0') {
            return { valid: true, convertedValue: false };
          }
        }
        return { valid: false, error: 'Invalid boolean format' };

      case 'text':
      case 'textarea':
      case 'email':
      case 'url':
      case 'tel':
        return { valid: true, convertedValue: String(value) };

      case 'select':
      case 'multiselect':
        return { valid: true, convertedValue: value };

      default:
        return { valid: true, convertedValue: value };
    }
  }

  /**
   * Formats value for display based on field type
   */
  static formatValue(value: any, fieldType: FieldType, locale = 'en-US'): string {
    if (value === null || value === undefined) return '';

    switch (fieldType) {
      case 'number':
        return new Intl.NumberFormat(locale).format(Number(value));

      case 'decimal':
        return new Intl.NumberFormat(locale, { 
          minimumFractionDigits: 2,
          maximumFractionDigits: 2 
        }).format(Number(value));

      case 'currency':
        return new Intl.NumberFormat(locale, { 
          style: 'currency',
          currency: 'USD' // Should be configurable
        }).format(Number(value));

      case 'percentage':
        return new Intl.NumberFormat(locale, { 
          style: 'percent',
          minimumFractionDigits: 2
        }).format(Number(value) / 100);

      case 'date':
        return new Date(value).toLocaleDateString(locale);

      case 'datetime':
        return new Date(value).toLocaleString(locale);

      default:
        return String(value);
    }
  }
}
